import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const NAV_LINKS = [
  { key: "about", href: "/#about" },
  { key: "projects", href: "/#projects" },
  { key: "vision", href: "/#vision" },
  { key: "insights", href: "/#insights" },
  { key: "contact", href: "/#contact" },
];

export const Navbar = () => {
  const { t, lang, setLang } = useLanguage();
  const [scrolled, setScrolled] = useState(() =>
    typeof window !== "undefined" ? window.scrollY > 12 : false
  );
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // close mobile menu when path changes (handled in link onClick)
  void location;

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/85 nav-blur border-b border-line"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link
            to="/"
            data-testid="brand-logo"
            className="font-serif-display text-xl md:text-2xl tracking-tight text-ink"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            Ivan Bautista
          </Link>

          <nav className="hidden md:flex items-center gap-10">
            {NAV_LINKS.map((l) => (
              <a
                key={l.key}
                href={l.href}
                data-testid={`nav-link-${l.key}`}
                className="text-[12px] font-medium tracking-[0.18em] uppercase text-ink-soft hover:text-ink transition-colors"
              >
                {t.nav[l.key]}
              </a>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <LanguageToggle lang={lang} setLang={setLang} />
            <a
              href="/#contact"
              data-testid="nav-cta-contact"
              className="text-[12px] font-medium tracking-[0.18em] uppercase text-white bg-navy hover:bg-navy-light transition-colors px-5 py-3"
            >
              {t.nav.contact}
            </a>
          </div>

          <button
            type="button"
            data-testid="mobile-menu-toggle"
            className="md:hidden p-2 -mr-2 text-ink"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu-panel"
          className="md:hidden border-t border-line bg-white"
        >
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-5">
            {NAV_LINKS.map((l) => (
              <a
                key={l.key}
                href={l.href}
                data-testid={`mobile-nav-link-${l.key}`}
                className="text-sm tracking-[0.18em] uppercase text-ink-soft"
                onClick={() => setOpen(false)}
              >
                {t.nav[l.key]}
              </a>
            ))}
            <div className="pt-3 border-t border-line">
              <LanguageToggle lang={lang} setLang={setLang} />
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

const LanguageToggle = ({ lang, setLang }) => (
  <div
    data-testid="language-toggle"
    className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-medium"
  >
    <button
      type="button"
      data-testid="lang-es"
      onClick={() => setLang("es")}
      className={`transition-colors ${
        lang === "es" ? "text-ink" : "text-muted-foreground hover:text-ink"
      }`}
      aria-pressed={lang === "es"}
    >
      ES
    </button>
    <span className="text-muted-foreground">·</span>
    <button
      type="button"
      data-testid="lang-en"
      onClick={() => setLang("en")}
      className={`transition-colors ${
        lang === "en" ? "text-ink" : "text-muted-foreground hover:text-ink"
      }`}
      aria-pressed={lang === "en"}
    >
      EN
    </button>
  </div>
);

export default Navbar;
